import { CountryCode } from "libphonenumber-js/types.cjs";
import postalCodes from "postal-codes-js";
import { getCountryCallingCode, isValidPhoneNumber } from "react-phone-number-input";

import { Button, Cascader, Checkbox, DatePicker, Form, Input, Select, Spin, message } from "antd";
import { RuleObject } from "antd/es/form";
import axios from "axios";
import dayjs from "dayjs";
import React, { useEffect, useState } from "react";

import styles from "@/components/Account/PersonalInformation/PersonalInformationEditForm/PersonalInformationEditForm.module.css";
import {
  DATE_FORMAT_DISPLAY,
  DATE_FORMAT_END_OF_DAY,
  DATE_FORMAT_START_OF_DAY,
  JSON_DATA,
  MYKAD_FIELDS,
  SINGPASS_FIELDS,
} from "@/constant";
import { useCountryOptions } from "@/hooks/useCountryoptions";
import { usePhoneOptions } from "@/hooks/usePhoneOptions";
import { updateCustomer } from "@/services/Auth/AuthApi";
import { getCitiesByState, getStatesByCountry } from "@/services/Location";
import { GetCustomerData } from "@/types/authService";

const { Option } = Select;

interface PersonalInformationEditFormProps {
  onClose?: (data?: GetCustomerData) => void;
  editData: GetCustomerData;
}

const CustomRow: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className={styles.row}>{children}</div>
);

const PersonalInformationEditForm: React.FC<PersonalInformationEditFormProps> = ({
  onClose,
  editData,
}) => {
  const [form] = Form.useForm();
  const phoneOptions = usePhoneOptions();
  const countryOptions = useCountryOptions();

  const [phoneNumberValue, setPhoneNumberValue] = useState("");
  const [countryCode, setCountryCode] = useState<string>("");
  const [loading, setLoading] = useState(false);
  const [nationalities, setNationalities] = useState<string[]>([]);
  const [ethnicities, setEthnicities] = useState<string[]>([]);
  const [states, setStates] = useState<string[]>([]);
  const [cities, setCities] = useState<string[]>([]);

  useEffect(() => {
    if (editData?.email) {
      form.setFieldsValue({
        name: editData.name,
        idType: editData.id_type,
        idNumber: editData.id_number,
        nationality: editData.nationality,
        ethnicity: editData.ethnicity,
        country: editData.country,
        city: editData.city,
        state: editData.state,
        date_of_birth: editData.date_of_birth ? dayjs(editData.date_of_birth) : null,
        sex: editData.sex,
        occupation: editData.occupation,
        occupation_details: editData.occupation_details,
        email: editData.email,
        country_code: editData.country_code,
        mobile_number: editData.mobile_number,
        dial_code: editData.dial_code,
        communication_medium: editData.communication_medium,
        two_fa_medium: editData.two_fa_medium,
        expiry_date: editData.expiry_date ? dayjs(editData.expiry_date) : null,
        tin_number: editData.tin_number,
        postal_code: editData.postal_code,
      });
      setPhoneNumberValue(editData.mobile_number);
      if (editData.country_code) {
        setCountryCode(editData.country_code);
      }
      if (editData.country) {
        void getStatesByCountry(editData.country).then((stateList) => {
          setStates(stateList);
          if (editData.state) {
            void getCitiesByState(editData.country, editData.state).then((cityList) => {
              setCities(cityList);
            });
          }
        });
      }
    }
  }, [editData, form]);

  useEffect(() => {
    const fetchNationalities = async () => {
      try {
        const res = await axios.get(JSON_DATA.NATIONALITIES);
        setNationalities(res.data);
      } catch (err) {
        console.error("Error fetching nationalities", err);
      }
    };

    void fetchNationalities();
  }, []);

  useEffect(() => {
    const fetchEthnicities = async () => {
      try {
        const res = await axios.get(JSON_DATA.ETHNICITIES);
        setEthnicities(res.data);
      } catch (err) {
        console.error("Error fetching ethnicities", err);
      }
    };

    void fetchEthnicities();
  }, []);

  const validatePhoneNumber = (rule: any, value: string): Promise<void> => {
    if (!value) return Promise.resolve();

    if (!countryCode) {
      return Promise.reject("Please select a country code");
    }

    try {
      const valid = isValidPhoneNumber(value, countryCode as CountryCode);
      if (valid) {
        return Promise.resolve();
      }
      return Promise.reject("Invalid phone number");
    } catch {
      return Promise.reject("Invalid phone number");
    }
  };

  const handleCountryChange = async (country: string) => {
    form.setFieldsValue({ state: undefined, city: undefined });
    setStates([]);
    setCities([]);
    form.setFields([
      {
        name: "postal_code",
        errors: [],
      },
    ]);

    if (country) {
      const stateList = await getStatesByCountry(country);
      setStates(stateList);
    }
  };

  const handleStateChange = async (state: string) => {
    const country = form.getFieldValue("country");
    form.setFieldsValue({ city: undefined });
    setCities([]);

    if (country && state) {
      const cityList = await getCitiesByState(country, state);
      setCities(cityList);
    }
  };

  const handleSubmit = async (values: GetCustomerData): Promise<void> => {
    setLoading(true);
    try {
      const dial_code = countryCode ? `+${getCountryCallingCode(countryCode as CountryCode)}` : "";
      const payload = {
        ...values,
        country_code: countryCode,
        dial_code: dial_code,
        date_of_birth: values.date_of_birth
          ? dayjs(values.date_of_birth).format("DD-MM-YYYY")
          : null,
        expiry_date: values.expiry_date
          ? dayjs(values.expiry_date).endOf("day").format(DATE_FORMAT_END_OF_DAY)
          : null,
      };
      await updateCustomer(String(editData.id), payload);
      message.success(`Profile updated successfully!`);

      onClose?.();
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const validatePostalCode = (_: RuleObject, value: string): Promise<void> => {
    const country = form.getFieldValue("country");

    if (!value) return Promise.resolve();

    if (country === "Singapore") {
      const result = postalCodes.validate("SG", value);
      return result === true ? Promise.resolve() : Promise.reject("Invalid Singapore postal code");
    }

    if (country === "Malaysia") {
      const result = postalCodes.validate("MY", value);
      return result === true ? Promise.resolve() : Promise.reject("Invalid Malaysia postal code");
    }

    return Promise.resolve();
  };

  const isFieldDisabled = (name: string): boolean => {
    const type = editData?.onboarding_type;
    if (type === "mykad") return MYKAD_FIELDS.includes(name);
    if (type === "singpass") return SINGPASS_FIELDS.includes(name);
    if (type === "form") return ["occupation", "occupation_details", "email"].includes(name);
    return false;
  };

  return (
    <div className={styles.mainContainer}>
      {loading ? (
        <Spin fullscreen />
      ) : (
        <div className={styles.personalInfoSection}>
          <Form form={form} layout="vertical" onFinish={handleSubmit} className={styles.form}>
            <CustomRow>
              <Form.Item name="name" label="Name" className={styles.formItem}>
                <Input
                  placeholder="Enter Name"
                  disabled={isFieldDisabled("name")}
                  data-dd-privacy="mask"
                />
              </Form.Item>

              <Form.Item label="ID Type" name="idType" className={styles.formItem}>
                <Select
                  placeholder="Select ID Type"
                  className={styles.customDropdown}
                  disabled
                  data-dd-privacy="mask"
                >
                  <Option value="nric">NRIC</Option>
                  <Option value="passport">Passport</Option>
                </Select>
              </Form.Item>
            </CustomRow>

            <CustomRow>
              <Form.Item label="ID Number" name="idNumber" className={styles.formItem}>
                <Input placeholder="Enter ID Number" disabled data-dd-privacy="mask" />
              </Form.Item>

              <Form.Item label="Nationality" name="nationality" className={styles.formItem}>
                <Select
                  placeholder="Select Nationality"
                  className={styles.customDropdown}
                  showSearch
                  optionFilterProp="children"
                  disabled={isFieldDisabled("nationality")}
                  data-dd-privacy="mask"
                >
                  {nationalities.map((nation) => (
                    <Option key={nation} value={nation}>
                      {nation}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </CustomRow>

            <CustomRow>
              <Form.Item label="Ethnicity" name="ethnicity" className={styles.formItem}>
                <Select
                  placeholder="Select Ethnicity"
                  className={styles.customDropdown}
                  showSearch
                  optionFilterProp="children"
                  disabled={isFieldDisabled("ethnicity")}
                  data-dd-privacy="mask"
                >
                  {ethnicities.map((ethnicity) => (
                    <Option key={ethnicity} value={ethnicity}>
                      {ethnicity}
                    </Option>
                  ))}
                </Select>
              </Form.Item>

              <Form.Item label="Country" name="country" className={styles.formItem}>
                <Select
                  placeholder="Select Country"
                  className={styles.customDropdown}
                  showSearch
                  optionFilterProp="value"
                  options={countryOptions}
                  onChange={handleCountryChange}
                  disabled={isFieldDisabled("country")}
                  data-dd-privacy="mask"
                />
              </Form.Item>
            </CustomRow>
            <CustomRow>
              <Form.Item label="State" name="state" className={styles.formItem}>
                <Select
                  placeholder="Select State"
                  className={styles.customDropdown}
                  onChange={handleStateChange}
                  disabled={isFieldDisabled("state")}
                  data-dd-privacy="mask"
                >
                  {states.map((state) => (
                    <Option key={state} value={state}>
                      {state}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
              <Form.Item label="City" name="city" className={styles.formItem}>
                <Select
                  placeholder="Select city"
                  className={styles.customDropdown}
                  disabled={isFieldDisabled("city")}
                  data-dd-privacy="mask"
                >
                  {cities.map((city) => (
                    <Option key={city} value={city}>
                      {city}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </CustomRow>
            <CustomRow>
              <Form.Item label="DOB" name="date_of_birth" className={styles.formItem}>
                <DatePicker
                  className={styles.customDatePicker}
                  format="DD-MM-YYYY"
                  disabled={isFieldDisabled("date_of_birth")}
                  data-dd-privacy="mask"
                />
              </Form.Item>
              <Form.Item label="Sex" name="sex" className={styles.formItem}>
                <Select
                  placeholder="Select Sex"
                  className={styles.customDropdown}
                  disabled={isFieldDisabled("sex")}
                  data-dd-privacy="mask"
                >
                  <Option value="Male">Male</Option>
                  <Option value="Female">Female</Option>
                </Select>
              </Form.Item>
            </CustomRow>
            <CustomRow>
              <Form.Item
                label="Mobile Number"
                name="mobile_number"
                required={false}
                rules={[
                  { required: true, message: "Please select a country code" },
                  { validator: validatePhoneNumber },
                ]}
                className={styles.formItem}
              >
                <Input
                  addonBefore={
                    <Cascader
                      options={phoneOptions}
                      value={countryCode ? [countryCode] : []}
                      onChange={(val) => {
                        const code = val?.[0] ?? "";
                        form.setFieldsValue({
                          country_code: code,
                          dial_code: code ? `+${getCountryCallingCode(code as CountryCode)}` : "",
                        });
                        setCountryCode(code);
                      }}
                      allowClear={false}
                      placeholder="Code"
                      style={{ width: 140, height: 45, border: "none" }}
                      disabled={isFieldDisabled("mobile_number")}
                    />
                  }
                  value={phoneNumberValue}
                  placeholder="Enter contact number"
                  onChange={(e) => {
                    setPhoneNumberValue(e.target.value);
                  }}
                  disabled={isFieldDisabled("mobile_number")}
                  data-dd-privacy="mask"
                />
              </Form.Item>
              <Form.Item label="Email" name="email" className={styles.formItem}>
                <Input
                  placeholder="Enter Email"
                  type="email"
                  disabled={isFieldDisabled("email")}
                  data-dd-privacy="mask"
                />
              </Form.Item>
            </CustomRow>

            <CustomRow>
              <Form.Item label="Occupation" name="occupation" className={styles.formItem}>
                <Input
                  placeholder="Enter Occupation"
                  disabled={isFieldDisabled("occupation")}
                  data-dd-privacy="mask"
                />
              </Form.Item>
              <Form.Item
                label="Occupation Details"
                name="occupation_details"
                className={styles.formItem}
              >
                <Input
                  placeholder="Enter Occupation Details"
                  disabled={isFieldDisabled("occupation_details")}
                  data-dd-privacy="mask"
                />
              </Form.Item>
            </CustomRow>

            <CustomRow>
              <Form.Item label="Expiration Date" name="expiry_date" className={styles.formItem}>
                <DatePicker
                  className={styles.customDatePicker}
                  format="DD-MM-YYYY"
                  disabled
                  data-dd-privacy="mask"
                />
              </Form.Item>
              <Form.Item label="Tin Number" name="tin_number" className={styles.formItem}>
                <Input placeholder="Enter TIN Number" data-dd-privacy="mask" />
              </Form.Item>
            </CustomRow>
            <CustomRow>
              <Form.Item
                label="Postal Code"
                name="postal_code"
                className={styles.formItem}
                rules={[
                  { required: true, message: "Postal code is required" },
                  { validator: validatePostalCode },
                ]}
              >
                <Input
                  placeholder="Enter Postal Code"
                  disabled={isFieldDisabled("postal_code")}
                  data-dd-privacy="mask"
                />
              </Form.Item>
            </CustomRow>

            <CustomRow>
              <Form.Item
                label="Communication Medium"
                name="communication_medium"
                className={styles.formItem}
                rules={[
                  {
                    required: true,
                    message: "Please select at least one communication medium",
                  },
                ]}
              >
                <Checkbox.Group className={styles.checkboxGroup}>
                  <Checkbox value="Email">Email</Checkbox>
                  <Checkbox value="WhatsApp">WhatsApp</Checkbox>
                  <Checkbox value="SMS">SMS</Checkbox>
                  <Checkbox value="Physical Letter">Physical Letter</Checkbox>
                </Checkbox.Group>
              </Form.Item>

              <Form.Item
                label="Preferred 2FA Medium For OTP"
                name="two_fa_medium"
                className={styles.formItem}
              >
                <Checkbox.Group className={styles.checkboxGroup}>
                  <Checkbox value="Email">Email</Checkbox>
                  <Checkbox value="WhatsApp">WhatsApp</Checkbox>
                  <Checkbox value="SMS">SMS</Checkbox>
                </Checkbox.Group>
              </Form.Item>
            </CustomRow>

            <div className={styles.buttonsWrapper}>
              <Button className={styles.clearButton} onClick={() => onClose?.()}>
                Cancel
              </Button>

              <Button htmlType="submit" className={styles.applyButton}>
                Save
              </Button>
            </div>
          </Form>
        </div>
      )}
    </div>
  );
};

export default PersonalInformationEditForm;
